import { FileText } from 'lucide-react'
import MovementStatus, { movementState } from '../components/MovementStatus'
/**
 * Arquivo: src/pages/ContasFinanceiras.tsx
 * Versão: 5.4.10
 * Data: 02/10/2026
 * Descrição: Visão unificada de Saídas e Entradas, sem formulário ou detalhe duplicado.
 */
import { useRegisterQuickCreate } from '../context/QuickCreateContext'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCircle2, Copy, Edit3, LoaderCircle, Paperclip, Plus, Search, SlidersHorizontal, Trash2 } from 'lucide-react'
import Modal from '../components/Modal'
import MoneyInput from '../components/MoneyInput'
import ConfirmDialog from '../components/ConfirmDialog'
import BalanceStrip from '../components/BalanceStrip'
import AnexosLancamento from '../components/AnexosLancamento'
import LancamentoDetalhesModal from '../components/LancamentoDetalhesModal'
import MobileActionSheet, { type MobileAction } from '../components/MobileActionSheet'
import AppSkeleton from '../components/AppSkeleton'
import MobileFilterSheet from '../components/MobileFilterSheet'
import {
  baixarLancamento,
  excluirLancamento,
  listarCategorias,
  listarContas,
  listarLancamentos,
  listarSubcategorias,
  obterAcessoBase,
  obterBaseAtiva,
  podeEscrever,
  podeExcluir,
  type FiltroLancamentos,
} from '../services/financeiro'
import { usePeriod } from '../context/PeriodContext'
import type {
  BaseFinanceira,
  Categoria,
  Conta,
  Lancamento,
  PerfilAcesso,
  Subcategoria,
  TipoLancamento,
} from '../types/database'

const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
const hoje = () => new Date().toISOString().slice(0, 10)
const dataBR = (valor?: string | null) => {
  if (!valor) return '—'
  const p = valor.slice(0, 10).split('-')
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : valor
}
const mascaraDataBR = (valor: string) => {
  const n = valor.replace(/\D/g, '').slice(0, 8)
  if (n.length <= 2) return n
  if (n.length <= 4) return `${n.slice(0, 2)}/${n.slice(2)}`
  return `${n.slice(0, 2)}/${n.slice(2, 4)}/${n.slice(4)}`
}
const dataBRParaISO = (valor: string) => {
  const m = valor.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (!m) return ''
  const [, dd, mm, aaaa] = m
  const iso = `${aaaa}-${mm}-${dd}`
  const d = new Date(`${iso}T12:00:00`)
  return d.getFullYear() === Number(aaaa) && d.getMonth() + 1 === Number(mm) && d.getDate() === Number(dd) ? iso : ''
}
const addDias = (data: string, dias: number) => {
  const d = new Date(`${data}T12:00:00`)
  d.setDate(d.getDate() + dias)
  return d.toISOString().slice(0, 10)
}
const numerosBoleto = (valor?: string | null) => String(valor || '').replace(/\D/g, '')

const chaveFiltroPersistente = (tipo: TipoLancamento) => `cf_filtro_${tipo === 'DESPESA' ? 'saidas' : 'entradas'}`
const telaMobile = () => typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches
const lerFiltroPersistente = (tipo: TipoLancamento): FiltroLancamentos => {
  if (telaMobile()) return { tipo }
  try {
    const salvo = localStorage.getItem(chaveFiltroPersistente(tipo))
    if (salvo) return { ...JSON.parse(salvo), tipo }
  } catch { /* ignora armazenamento inválido */ }
  return { tipo }
}

interface Props {
  tipo: TipoLancamento
}

type CardResumoContas = 'PENDENTES' | 'VENCIDAS' | 'HOJE' | 'PROXIMOS30'

export default function ContasFinanceiras({ tipo }: Props) {
  const { periodo, inicio, fim } = usePeriod()
  const navigate = useNavigate()
  const pagar = tipo === 'DESPESA'
  const titulo = pagar ? 'Saídas' : 'Entradas'
  const singular = pagar ? 'saída' : 'entrada'

  const [base, setBase] = useState<BaseFinanceira | null>(null)
  const [perfil, setPerfil] = useState<PerfilAcesso | null>(null)
  const [lista, setLista] = useState<Lancamento[]>([])
  const [contas, setContas] = useState<Conta[]>([])
  const [cats, setCats] = useState<Categoria[]>([])
  const [subs, setSubs] = useState<Subcategoria[]>([])
  const [filtro, setFiltro] = useState<FiltroLancamentos>(() => lerFiltroPersistente(tipo))
  const [filtroInicioBR, setFiltroInicioBR] = useState(() => (lerFiltroPersistente(tipo).inicio ? dataBR(lerFiltroPersistente(tipo).inicio) : ''))
  const [filtroFimBR, setFiltroFimBR] = useState(() => (lerFiltroPersistente(tipo).fim ? dataBR(lerFiltroPersistente(tipo).fim) : ''))
  const [baixa, setBaixa] = useState<Lancamento | null>(null)
  const [baixaValor, setBaixaValor] = useState(0)
  const [excluirPendente, setExcluirPendente] = useState<Lancamento | null>(null)
  const [anexos, setAnexos] = useState<Lancamento | null>(null)
  const [mobileSelecionado, setMobileSelecionado] = useState<Lancamento | null>(null)
  const [filtrosAbertos, setFiltrosAbertos] = useState(false)
  const [filtroDraft, setFiltroDraft] = useState<FiltroLancamentos>({ tipo })
  const [draftInicioBR, setDraftInicioBR] = useState('')
  const [draftFimBR, setDraftFimBR] = useState('')
  const [cardResumo, setCardResumo] = useState<CardResumoContas | null>(null)
  const [detalhe, setDetalhe] = useState<Lancamento | null>(null)
  const [erro, setErro] = useState('')
  const [aviso, setAviso] = useState('')
  const [loading, setLoading] = useState(true)
  const [salvando, setSalvando] = useState(false)

  useEffect(() => { if (baixa) setBaixaValor(Number(baixa.valor_previsto || 0)) }, [baixa])

  const carregar = async (f = filtro) => {
    setErro('')
    try {
      const b = base || await obterBaseAtiva()
      if (!b) throw new Error('Base não localizada.')
      const filtroTipo: FiltroLancamentos = {
        ...f,
        tipo,
        ...(periodo !== 'ALL' ? { inicio: f.inicio || inicio, fim: f.fim || fim } : {}),
      }
      const [l, c, ca, a] = await Promise.all([
        listarLancamentos(b.id, filtroTipo),
        listarContas(b.id),
        listarCategorias(b.id),
        obterAcessoBase(b.id),
      ])
      const s = await listarSubcategorias(b.id, ca)
      setBase(b)
      setLista(l)
      setContas(c)
      setCats(ca)
      setSubs(s)
      setPerfil(a?.perfil || null)
    } catch (e) {
      setErro(e instanceof Error ? e.message : `Erro ao carregar ${titulo.toLowerCase()}.`)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const salvo = lerFiltroPersistente(tipo)
    setFiltro(salvo)
    setFiltroInicioBR(salvo.inicio ? dataBR(salvo.inicio) : '')
    setFiltroFimBR(salvo.fim ? dataBR(salvo.fim) : '')
    void carregar(salvo)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo, periodo])

  useEffect(() => {
    try { localStorage.setItem(chaveFiltroPersistente(tipo), JSON.stringify({ ...filtro, tipo })) } catch { /* storage indisponível */ }
  }, [filtro, tipo])

  const principal = contas.find((c) => c.principal && c.ativa)
  const listaVisivel = useMemo(() => lista.filter((l) => l.status !== 'CANCELADO'), [lista])

  const resumo = useMemo(() => {
    const pendentes = listaVisivel.filter((l) => l.status === 'PENDENTE')
    const h = hoje()
    const limite30 = addDias(h, 30)
    const soma = (arr: Lancamento[]) => arr.reduce((s, l) => s + Number(l.valor_previsto || 0), 0)
    return {
      pendente: soma(pendentes),
      vencidas: soma(pendentes.filter((l) => !!l.vencimento && l.vencimento < h)),
      hoje: soma(pendentes.filter((l) => l.vencimento === h)),
      proximos30: soma(pendentes.filter((l) => !!l.vencimento && l.vencimento > h && l.vencimento <= limite30)),
    }
  }, [listaVisivel])

  const composicaoCard = useMemo(() => {
    if (!cardResumo) return [] as Lancamento[]
    const h = hoje()
    const limite30 = addDias(h, 30)
    const pendentes = listaVisivel.filter((l) => l.status === 'PENDENTE')
    if (cardResumo === 'PENDENTES') return pendentes
    if (cardResumo === 'VENCIDAS') return pendentes.filter((l) => !!l.vencimento && l.vencimento < h)
    if (cardResumo === 'HOJE') return pendentes.filter((l) => l.vencimento === h)
    return pendentes.filter((l) => !!l.vencimento && l.vencimento > h && l.vencimento <= limite30)
  }, [listaVisivel, cardResumo])

  const tituloCardResumo = cardResumo === 'PENDENTES'
    ? 'Composição dos previstos'
    : cardResumo === 'VENCIDAS'
      ? 'Composição das vencidas'
      : cardResumo === 'HOJE'
        ? 'Vencimentos de hoje'
        : 'Próximos 30 dias'

  const totalCardResumo = cardResumo === 'PENDENTES'
    ? resumo.pendente
    : cardResumo === 'VENCIDAS'
      ? resumo.vencidas
      : cardResumo === 'HOJE'
        ? resumo.hoje
        : resumo.proximos30

  const resumoFiltro = useMemo(() => ({
    quantidade: listaVisivel.length,
    valor: listaVisivel.reduce(
      (soma, l) => soma + Number(['PAGO', 'RECEBIDO'].includes(l.status)
        ? (l.valor_realizado ?? l.valor_previsto ?? 0)
        : (l.valor_previsto ?? 0)),
      0,
    ),
  }), [listaVisivel])

  const abrirNovo = () => navigate(`/lancamentos?novo=${pagar ? 'despesa' : 'receita'}`, { state: { financialOrigin: pagar ? '/pagar' : '/receber' } })
  useRegisterQuickCreate(pagar ? 'Nova saída' : 'Nova entrada', abrirNovo, podeEscrever(perfil))
  const abrirEdicao = (l: Lancamento) => navigate(`/lancamentos?editar=${encodeURIComponent(l.id)}`)

  const copiarCodigoBarras = async (l: Lancamento) => {
    const codigo = numerosBoleto(l.codigo_barras)
    if (!codigo) return
    try {
      await navigator.clipboard.writeText(codigo)
      setAviso('Código do boleto copiado.')
      window.setTimeout(() => setAviso(''), 1800)
    } catch {
      setErro('Não foi possível copiar o código do boleto.')
    }
  }

  const confirmarBaixa = async () => {
    if (!base || !baixa) return
    const conta = (document.getElementById('cfBaixaConta') as HTMLSelectElement)?.value
    const data = (document.getElementById('cfBaixaData') as HTMLInputElement)?.value
    const valor = baixaValor
    setSalvando(true)
    setErro('')
    try {
      await baixarLancamento(base.id, baixa.id, tipo, conta, data, valor)
      setBaixa(null)
      await carregar()
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Erro na baixa.')
    } finally {
      setSalvando(false)
    }
  }

  const mobileActions = (l: Lancamento): MobileAction[] => {
    const a: MobileAction[] = [{ label: 'Ver detalhes', icon: <FileText size={18}/>, onClick: () => setDetalhe(l) }]
    if (podeEscrever(perfil)) {
      a.push({ label: 'Editar', icon: <Edit3 size={18}/>, onClick: () => abrirEdicao(l) })
    }
    if (pagar && numerosBoleto(l.codigo_barras)) {
      a.push({ label: 'Copiar código do boleto', icon: <Copy size={18}/>, onClick: () => void copiarCodigoBarras(l) })
    }
    a.push({ label: 'Comprovantes e anexos', icon: <Paperclip size={18}/>, onClick: () => setAnexos(l) })
    if (podeEscrever(perfil) && l.status === 'PENDENTE') {
      a.push({ label: pagar ? 'Pagar' : 'Receber', icon: <CheckCircle2 size={18}/>, onClick: () => setBaixa(l) })
    }
    if (podeExcluir(perfil)) {
      a.push({ label: 'Excluir', icon: <Trash2 size={18}/>, danger: true, onClick: () => setExcluirPendente(l) })
    }
    return a
  }

  const abrirFiltros = () => {
    setFiltroDraft({ ...filtro, tipo })
    setDraftInicioBR(filtro.inicio ? dataBR(filtro.inicio) : '')
    setDraftFimBR(filtro.fim ? dataBR(filtro.fim) : '')
    setFiltrosAbertos(true)
  }

  const aplicarFiltrosMobile = () => {
    const proximo: FiltroLancamentos = {
      ...filtro,
      ...filtroDraft,
      tipo,
      inicio: dataBRParaISO(draftInicioBR) || undefined,
      fim: dataBRParaISO(draftFimBR) || undefined,
    }
    setFiltro(proximo)
    setFiltroInicioBR(draftInicioBR)
    setFiltroFimBR(draftFimBR)
    setFiltrosAbertos(false)
    void carregar(proximo)
  }

  const limparFiltrosMobile = () => {
    const limpo: FiltroLancamentos = { tipo, texto: filtro.texto || '' }
    setFiltroDraft(limpo)
    setDraftInicioBR('')
    setDraftFimBR('')
    setFiltro(limpo)
    setFiltroInicioBR('')
    setFiltroFimBR('')
    setFiltrosAbertos(false)
    void carregar(limpo)
  }

  const filtrosAtivos = Number(!!filtro.status) + Number(!!filtro.inicio) + Number(!!filtro.fim)

  if (loading) return <AppSkeleton variant="list" />

  return (
    <section className={`financial-page ${pagar ? 'expense' : 'income'}`}>
      <div className="page-heading">
        <div>
          <h1>{titulo}</h1>
          <p>{pagar ? 'Todas as saídas, previstas e realizadas' : 'Todas as entradas, previstas e realizadas'} da {base?.nome}.</p>
        </div>
        {podeEscrever(perfil) && (
          <button className="button primary compact" onClick={abrirNovo}>
            <Plus size={16} /> {pagar ? 'Nova saída' : 'Nova entrada'}
          </button>
        )}
      </div>

      {erro && <div className="error-box page-error">{erro}</div>}
      {aviso && <div className="success-box page-error">{aviso}</div>}

      <BalanceStrip baseId={base?.id} />

      <section className={`financial-mobile-summary ${pagar ? 'expense' : 'income'}`}>
        <button type="button" className="financial-mobile-main" onClick={() => setCardResumo('PENDENTES')}>
          <span>Previsto</span>
          <strong className="privacy-value">{brl.format(resumo.pendente)}</strong>
          <small>Valor ainda não realizado</small>
        </button>
        <div className="financial-mobile-stats">
          <button type="button" onClick={() => setCardResumo('VENCIDAS')}><span>Vencidas</span><strong className="privacy-value">{brl.format(resumo.vencidas)}</strong></button>
          <button type="button" onClick={() => setCardResumo('HOJE')}><span>Hoje</span><strong className="privacy-value">{brl.format(resumo.hoje)}</strong></button>
          <button type="button" onClick={() => setCardResumo('PROXIMOS30')}><span>30 dias</span><strong className="privacy-value">{brl.format(resumo.proximos30)}</strong></button>
        </div>
      </section>

      <div className="cards-grid cards-4 financial-summary financial-desktop-summary">
        <button type="button" className="metric-card summary-card-button" onClick={() => setCardResumo('PENDENTES')}><span>Previsto</span><strong className="privacy-value">{brl.format(resumo.pendente)}</strong><small>Toque para ver a composição.</small></button>
        <button type="button" className="metric-card summary-card-button danger-card" onClick={() => setCardResumo('VENCIDAS')}><span>Vencidas</span><strong className="privacy-value">{brl.format(resumo.vencidas)}</strong><small>Toque para ver a composição.</small></button>
        <button type="button" className="metric-card summary-card-button today-card" onClick={() => setCardResumo('HOJE')}><span>Vencem hoje</span><strong className="privacy-value">{brl.format(resumo.hoje)}</strong><small>Toque para ver a composição.</small></button>
        <button type="button" className="metric-card summary-card-button" onClick={() => setCardResumo('PROXIMOS30')}><span>Próximos 30 dias</span><strong className="privacy-value">{brl.format(resumo.proximos30)}</strong><small>Toque para ver a composição.</small></button>
      </div>

      <div className="mobile-finance-toolbar">
        <div className="search-field mobile-finance-search">
          <Search size={16} />
          <input
            placeholder="Buscar lançamento"
            value={filtro.texto || ''}
            onChange={(e) => setFiltro({ ...filtro, texto: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter') void carregar() }}
          />
        </div>
        <button type="button" className={`mobile-filter-trigger${filtrosAtivos ? ' has-filters' : ''}`} onClick={abrirFiltros} aria-label="Abrir filtros">
          <SlidersHorizontal size={18}/>{filtrosAtivos > 0 && <span>{filtrosAtivos}</span>}
        </button>
      </div>

      <div className="filter-bar accounts-filter desktop-finance-filter">
        <div className="search-field"><Search size={16} /><input placeholder="Buscar descrição..." value={filtro.texto || ''} onChange={(e) => setFiltro({ ...filtro, texto: e.target.value })} /></div>
        <select value={filtro.status || ''} onChange={(e) => setFiltro({ ...filtro, status: e.target.value })}>
          <option value="">Todos os status</option>
          <option value="PENDENTE">Previsto</option>
          {pagar ? <option value="PAGO">Realizado</option> : <option value="RECEBIDO">Realizado</option>}
        </select>
        <input type="text" inputMode="numeric" placeholder="Data inicial" value={filtroInicioBR} onChange={(e) => { const valor = mascaraDataBR(e.target.value); setFiltroInicioBR(valor); setFiltro({ ...filtro, inicio: dataBRParaISO(valor) }) }} />
        <input type="text" inputMode="numeric" placeholder="Data final" value={filtroFimBR} onChange={(e) => { const valor = mascaraDataBR(e.target.value); setFiltroFimBR(valor); setFiltro({ ...filtro, fim: dataBRParaISO(valor) }) }} />
        <button className="button ghost compact" onClick={() => void carregar()}>Filtrar</button>
      </div>

      <div className="filter-result-summary" aria-live="polite">
        <span><b>{resumoFiltro.quantidade}</b> {resumoFiltro.quantidade === 1 ? 'lançamento' : 'lançamentos'}</span>
        <i aria-hidden="true" />
        <span>Total <strong className="privacy-value">{brl.format(resumoFiltro.valor)}</strong></span>
      </div>

      <div className="mobile-record-list mobile-only-list">
        {listaVisivel.map((l) => {
          const vencida = l.status === 'PENDENTE' && !!l.vencimento && l.vencimento < hoje()
          const realizado = ['PAGO', 'RECEBIDO'].includes(l.status)
          const previsto = l.status === 'PENDENTE'
          const categoria = cats.find((c) => c.id === l.categoria_id)
          return <div key={l.id} data-movement-kind={pagar?'expense':'income'} data-movement-state={movementState(l.status,l.vencimento)} className={`mobile-record-item mobile-record-interactive movement-clean ${vencida ? 'is-overdue' : realizado ? 'is-realized' : previsto ? 'is-forecast' : ''}`}>
            <button type="button" className="mobile-record-open" onClick={() => setMobileSelecionado(l)} aria-label={`Ações para ${l.descricao}`}>
              <span className="mobile-record-main"><strong>{l.descricao}</strong><small>{dataBR(l.vencimento)} · {categoria?.nome || 'Sem categoria'}</small></span>
              <span className="mobile-record-side"><strong className={`${pagar ? 'expense' : 'income'} privacy-value`}>{brl.format(Number(realizado ? (l.valor_realizado ?? l.valor_previsto ?? 0) : (l.valor_previsto ?? 0)))}</strong><MovementStatus kind={pagar?'expense':'income'} status={l.status} date={l.vencimento}/></span>
            </button>
            
          </div>
        })}
        {!listaVisivel.length && <div className="mobile-empty-state">Nenhum registro encontrado.</div>}
      </div>

      <div className="panel desktop-list-panel">
        <div className="panel-header"><h2>{pagar ? 'Saídas' : 'Entradas'}</h2><span>{listaVisivel.length} registro(s)</span></div>
        <div className="table-wrap"><table className="ledger-table"><thead><tr><th>Vencimento</th><th>Descrição</th><th>Conta</th><th>Valor</th></tr></thead><tbody>
          {listaVisivel.map((l) => {
            const c = cats.find((x) => x.id === l.categoria_id)
            const conta = contas.find((x) => x.id === (l.status === 'PENDENTE' ? l.conta_prevista_id : l.conta_realizada_id || l.conta_prevista_id))
            const vencida = l.status === 'PENDENTE' && !!l.vencimento && l.vencimento < hoje()
            const realizado = ['PAGO', 'RECEBIDO'].includes(l.status)
            const previsto = l.status === 'PENDENTE'
            return <tr key={l.id} data-movement-kind={pagar?'expense':'income'} data-movement-state={movementState(l.status,l.vencimento)} className={`ledger-row movement-clean movement-type-${pagar ? 'expense' : 'income'} ${vencida ? 'row-overdue' : realizado ? 'row-realized' : previsto ? 'row-forecast' : ''}`} tabIndex={0} role="button" aria-label={`Ações para ${l.descricao}`} onClick={() => setMobileSelecionado(l)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setMobileSelecionado(l) } }}>
              <td><strong>{dataBR(l.vencimento)}</strong><small>{c?.nome || 'Sem categoria'}</small></td>
              <td><strong>{l.descricao}</strong>{l.pj&&<small>PJ</small>}</td>
              <td><strong>{conta ? `${conta.instituicao || ''} • ${conta.nome || ''}` : 'Conta não definida'}</strong></td>
              <td className={`${pagar ? 'expense' : 'income'} privacy-value`}><strong>{brl.format(Number(['PAGO', 'RECEBIDO'].includes(l.status) ? l.valor_realizado ?? l.valor_previsto ?? 0 : l.valor_previsto ?? 0))}</strong><MovementStatus kind={pagar?'expense':'income'} status={l.status} date={l.vencimento}/></td>
            </tr>
          })}
          {!listaVisivel.length && <tr><td colSpan={4}>Nenhum registro encontrado.</td></tr>}
        </tbody></table></div>
      </div>

      <Modal aberto={!!cardResumo} titulo={tituloCardResumo} subtitulo={`${composicaoCard.length} ${composicaoCard.length === 1 ? singular : `${singular}s`} no filtro atual`} largo onClose={() => setCardResumo(null)}>
        <div className="card-composition-sheet">
          <div className="card-composition-total"><span>Total</span><strong className={`privacy-value ${pagar ? 'expense' : 'income'}`}>{brl.format(totalCardResumo)}</strong></div>
          <div className="card-composition-list">
            {composicaoCard.map((l) => {
              const categoria = cats.find((c) => c.id === l.categoria_id)
              return <button type="button" key={l.id} onClick={() => { setCardResumo(null); setDetalhe(l) }}><span><strong>{l.descricao}</strong><small>{dataBR(l.vencimento)} · {categoria?.nome || 'Sem categoria'} · Previsto</small></span><b className={`privacy-value ${pagar ? 'expense' : 'income'}`}>{brl.format(Number(l.valor_previsto || 0))}</b></button>
            })}
            {!composicaoCard.length && <div className="mobile-empty-state">Nenhum lançamento compõe este card no filtro atual.</div>}
          </div>
        </div>
      </Modal>

      <MobileFilterSheet
        aberto={filtrosAbertos}
        titulo={`Filtrar ${titulo.toLowerCase()}`}
        onClose={() => setFiltrosAbertos(false)}
        onApply={aplicarFiltrosMobile}
        onClear={limparFiltrosMobile}
      >
        <label>Status
          <select value={filtroDraft.status || ''} onChange={(e) => setFiltroDraft({ ...filtroDraft, status: e.target.value || undefined })}>
            <option value="">Todos</option>
            <option value="PENDENTE">Previsto</option>
            {pagar ? <option value="PAGO">Realizado</option> : <option value="RECEBIDO">Realizado</option>}
          </select>
        </label>
        <label>Data inicial
          <input type="text" inputMode="numeric" placeholder="dd/mm/aaaa" value={draftInicioBR} onChange={(e) => setDraftInicioBR(mascaraDataBR(e.target.value))}/>
        </label>
        <label>Data final
          <input type="text" inputMode="numeric" placeholder="dd/mm/aaaa" value={draftFimBR} onChange={(e) => setDraftFimBR(mascaraDataBR(e.target.value))}/>
        </label>
      </MobileFilterSheet>

      <MobileActionSheet aberto={!!mobileSelecionado} titulo={mobileSelecionado?.descricao || singular} subtitulo={mobileSelecionado ? `${dataBR(mobileSelecionado.vencimento)} · ${brl.format(Number(mobileSelecionado.valor_previsto || 0))}` : undefined} actions={mobileSelecionado ? mobileActions(mobileSelecionado) : []} onClose={() => setMobileSelecionado(null)} />

      <LancamentoDetalhesModal
        lancamento={detalhe}
        categorias={cats}
        subcategorias={subs}
        contas={contas}
        podeEditar={podeEscrever(perfil)}
        onClose={() => setDetalhe(null)}
        onEditar={(atual) => { setDetalhe(null); abrirEdicao(atual) }}
        onErro={setErro}
      />

      <ConfirmDialog aberto={!!excluirPendente} titulo={`Excluir ${singular}`} mensagem={excluirPendente ? `Deseja realmente excluir “${excluirPendente.descricao}”?` : `Deseja realmente excluir esta ${singular}?`} confirmarTexto="Excluir" processando={salvando} onCancelar={() => setExcluirPendente(null)} onConfirmar={async () => {
        if (!base || !excluirPendente) return
        setSalvando(true); setErro('')
        try { await excluirLancamento(base.id, excluirPendente.id); setExcluirPendente(null); await carregar() }
        catch (e) { setErro(e instanceof Error ? e.message : `Erro ao excluir ${singular}.`) }
        finally { setSalvando(false) }
      }} />

      {base && anexos && <AnexosLancamento aberto={!!anexos} baseId={base.id} modulo="LANCAMENTO" registroId={anexos.id} titulo={anexos.descricao} podeEditar={podeEscrever(perfil)} onClose={() => setAnexos(null)} />}

      <Modal aberto={!!baixa} titulo={pagar ? 'Confirmar pagamento' : 'Confirmar recebimento'} subtitulo={baixa?.descricao} onClose={() => setBaixa(null)}>
        {baixa && <div className={`settlement-summary ${pagar ? 'expense' : 'income'}`}>
          <span><CheckCircle2 size={20}/></span>
          <div><small>{pagar ? 'Valor a pagar' : 'Valor a receber'}</small><strong className="privacy-value">{brl.format(baixaValor)}</strong></div>
        </div>}
        <div className="form-grid two settlement-form">
          <label>Conta<select id="cfBaixaConta" defaultValue={baixa?.conta_prevista_id || principal?.id || ''}><option value="">Selecione</option>{contas.filter((c) => c.ativa).map((c) => <option key={c.id} value={c.id}>{c.instituicao} • {c.nome}</option>)}</select></label>
          <label>Data<input id="cfBaixaData" type="date" defaultValue={hoje()} /></label>
          <label className="span-2">Valor realizado<MoneyInput value={baixaValor} onChange={setBaixaValor} /></label>
        </div>
        <div className="modal-actions"><button className="button ghost" disabled={salvando} onClick={() => setBaixa(null)}>Cancelar</button><button className="button primary" disabled={salvando} onClick={() => void confirmarBaixa()}>{salvando ? <><LoaderCircle className="button-spinner" size={16}/> Confirmando...</> : <><CheckCircle2 size={16}/> {pagar ? 'Confirmar pagamento' : 'Confirmar recebimento'}</>}</button></div>
      </Modal>
    </section>
  )
}

